import { UAParser } from 'ua-parser-js';
import Cookies from 'universal-cookie';
import { v4 as uuidv4 } from 'uuid';
import { IParsedUA, EWebViewActions } from '@/types';
import { NO_INDEX_ROUTES } from '@/constants/routes';
import { isClient } from '@/utils/common';
import {
    LOCAL_STORAGE_KEYS,
    SESSION_STORAGE_KEYS,
} from '@/constants/storageKeys';
import { Coupon } from '@/types';
import { useWebviewCallback } from '@/utils/webviewHandlers';
import { IncomingMessage } from 'http';
import { fetchGarageTimelineServer } from '@/utils/api';

export function removeUndefined(obj: any) {
    if (typeof obj !== 'object') {
        return obj;
    }

    Object.keys(obj).forEach((key) => {
        if (obj[key] && typeof obj[key] === 'object') {
            removeUndefined(obj[key]);
        } else if (obj[key] === undefined) {
            // eslint-disable-next-line no-param-reassign
            delete obj[key];
        }
    });

    return obj;
}

export function getParsedUA(ua: string): IParsedUA {
    const result = new UAParser(ua);

    const parsedUA: IParsedUA = {
        isMobile: result.getDevice().type === UAParser.DEVICE.MOBILE,
        device: result.getDevice().type as keyof typeof UAParser.DEVICE,
        browser: result.getBrowser().name || '',
    };

    return removeUndefined(parsedUA);
}
import {
    ETrackingStages,
    PageRoutes,
    getRoute,
} from '@/constants/trackingStages';
import { checkIfMultipleDamageFlow } from '@/components/estimateApproval/utils';

export const getDamageDataFromTimeline = (
    timeline: any,
    isPending: boolean = false
) => {
    if (isPending) {
        const pending =
            timeline?.latest_stage_details?.data?.pending_damages ?? [];
        const approved =
            timeline?.latest_stage_details?.data?.approved_damages ?? [];
        return [...pending, ...approved];
    }
    return (
        timeline?.timeline?.filter((t: any) => {
            if (t.name === ETrackingStages.REPAIR_IN_PROGRESS) {
                return t;
            }
        })[0]?.data?.damages || []
    );
};

export const cleanRegNo = (carNumber: string) =>
    carNumber?.toUpperCase()?.replace(/[. ,:-]+/g, '');

export const getFormattedRegNo = (registrationNo: string) => {
    if (!registrationNo) return '';
    let firstPart = '';
    let secondPart = '';
    let thirdPart = '';
    let fourthPart = '';
    let unformattedRegNo = cleanRegNo(registrationNo);
    if (unformattedRegNo?.includes('BH')) {
        firstPart = unformattedRegNo?.substring(0, 2);
        secondPart = unformattedRegNo?.substring(2, 4);
        thirdPart = unformattedRegNo?.substring(4, 8);
        fourthPart = unformattedRegNo?.substring(8, unformattedRegNo?.length);
    } else {
        firstPart = unformattedRegNo?.substring(0, 2);
        secondPart = unformattedRegNo?.substring(2, 4);
        thirdPart = unformattedRegNo?.substring(
            4,
            unformattedRegNo?.length - 4
        );
        fourthPart = unformattedRegNo?.substring(unformattedRegNo?.length - 4);
    }
    return firstPart + ' ' + secondPart + ' ' + thirdPart + ' ' + fourthPart;
};

export const getFirstName = (fullName: string) => {
    const firstName =
        fullName?.split(' ').length > 1 ? fullName.split(' ')[0] : fullName;
    return capitalizeName(firstName) || '';
};

function capitalizeName(name: string): string {
    if (!name) return '';

    return name.trim().charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

export const checkIfEmpty = (obj: object) => {
    return Object.values(obj).filter((v) => {
        return !v;
    }).length > 0
        ? true
        : false;
};
export const getCost = (timeline: any) => {
    const damageData = getDamageDataFromTimeline(timeline);

    const actualCostArr = ['SA APPROVED', 'AUTO APPROVED', 'CUSTOMER APPROVED'];
    const estimatedArr = ['SA APPROVED', 'CUSTOMER APPROVED'];
    let actualCost = 0;
    let estimatedCost = 0;
    damageData.forEach((d: any) => {
        d.parts.forEach((p: any) => {
            if (actualCostArr.includes(p.metadata.customer_approval_status)) {
                actualCost =
                    actualCost +
                    p.breakup.net_labour_cost +
                    p.breakup.net_part_cost +
                    p.breakup.net_service_cost;
            }
            if (estimatedArr.includes(p.metadata.customer_approval_status)) {
                estimatedCost =
                    estimatedCost +
                    p.breakup.net_labour_cost +
                    p.breakup.net_part_cost +
                    p.breakup.net_service_cost;
            }
        });
    });
    const increase = actualCost - estimatedCost;
    const percent = Math.max(
        Math.floor((increase / estimatedCost) * 100) || 0,
        0
    );

    return { actualCost, estimatedCost, percent };
};

export const getPaymentPendingDescription = (timelineData: any) => {
    const { percent } = getCost(timelineData);
    return `It’s slightly more than the estimate, but within the 15% variable cost mentioned earlier.`;
};

export function isWelcomeScreenViewed(garageState: any): boolean {
    const isViewed =
        garageState?.timeline?.timeline_metadata?.is_welcome_screen_viewed;
    return isViewed;
}

export const formatColor = (color: string | string[] | undefined): string => {
    if (!color) return 'N/A';
    return Array.isArray(color) ? color.join(', ') : color;
};

export const isQrOnlineCampaign = (): boolean => {
    if (typeof window === 'undefined') return false;

    const urlParams = new URLSearchParams(window.location.search);
    const utm_params = urlParams.get('utm_medium');

    return utm_params?.toLowerCase() === 'qr_online_campaign';
};

export async function fetchFromApi<T>(
    url: string,
    options: RequestInit = {}
): Promise<T> {
    try {
        const response = await fetch(url, {
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            ...options,
        });
        console.log('debug', response);

        if (!response.ok) {
            throw new Error(
                `API returned ${response.status}: ${response.statusText}`
            );
        }

        const data = await response.json();
        console.log('debug', data);
        return data;
    } catch (error) {
        console.error('Error fetching data:', error);
        throw error;
    }
}

export function getUserId() {
    let userId;
    if (typeof window !== 'undefined') {
        let cookies = new Cookies();
        userId = cookies.get('trackerid');
    }

    if (!userId) {
        console.log('No tracker id found in cookies');

        try {
            userId = crypto.randomUUID();
            if (!userId || userId?.length === 0) {
                userId = uuidv4();
            }
        } catch (_e) {
            userId = uuidv4();
        }
    }

    return userId;
}

export const isNonIndexableRoute = (pathName: string) =>
    NO_INDEX_ROUTES.some((route: string) => pathName.includes(route));

export const isIndexableRoute = (pathName: string): boolean => {
    const segments = pathName
        .split('/')
        .filter((segment) => segment.length > 0);

    const staticRoutes = [
        'ad-services',
        'api',
        'approved',
        'coming-soon',
        'error',
        'estimate-approval',
        'home',
        'job-cards',
        'no-selection',
        'notify-me',
        'pick-and-drop',
        'pick-and-drop-near-me',
        'qr-campaign',
        'repair-updates',
        'repeat-booking',
        'review-repair',
        'service-center',
        'service-estimate',
        'service-reminder',
        'stage-redirect',
        'tracking',
        'v1',
        '_error',
        '404',
        'rewards',
        'video-offer',
    ];

    // Check if first segment is a non-seo route
    const firstSegment = segments[0];
    if (staticRoutes.includes(firstSegment)) {
        return false;
    }

    return true;
};

//Adding expiry since session storage doesnt get removed when chrome is set to "Continue where left off"
export function setReferrer(val: string) {
    if (isClient()) {
        const date = new Date();
        date.setHours(date.getHours() + 24);
        sessionStorage.setItem(SESSION_STORAGE_KEYS.REFERRER_ADSC, val);
        sessionStorage.setItem(
            SESSION_STORAGE_KEYS.REFERRER_ADSC_EXPIRY_DATE,
            JSON.stringify(date)
        );
    }
}

export function getReferrer() {
    if (isClient()) {
        let referrer = sessionStorage.getItem(
            SESSION_STORAGE_KEYS.REFERRER_ADSC
        );
        const expiryDate = sessionStorage.getItem(
            SESSION_STORAGE_KEYS.REFERRER_ADSC_EXPIRY_DATE
        );
        if (expiryDate) {
            const newDate = JSON.stringify(new Date());
            if (newDate > expiryDate) {
                sessionStorage.removeItem(
                    SESSION_STORAGE_KEYS.REFERRER_ADSC_EXPIRY_DATE
                );
                sessionStorage.removeItem(SESSION_STORAGE_KEYS.REFERRER_ADSC);
                referrer = '';
            }
        }
        return referrer;
    } else {
        return '';
    }
}

export const getVehicleImageUrl = (
    makeName: string,
    modelName: string
): string => {
    return `https://ackodrive-prod.ackoassets.com/image/${makeName}/${modelName}/default/Hero-Transparent.png`;
};

export const getCouponMessage = (
    isLoggedIn: boolean,
    isGarageIdAvailable: boolean,
    matchingCoupons: Coupon[],
    rewardMessage: string
): string => {
    if (isGarageIdAvailable && isLoggedIn && matchingCoupons.length === 1) {
        return `${matchingCoupons[0].title} : ${matchingCoupons[0].description}`;
    }
    return rewardMessage;
};

export const clearPincodeAndGarageIdFromLocalStorage = () => {
    if (!isClient()) return;

    if (localStorage.getItem(LOCAL_STORAGE_KEYS.PINCODE)) {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.PINCODE);
    }

    if (localStorage.getItem(LOCAL_STORAGE_KEYS.GARAGE_ID)) {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.GARAGE_ID);
    }
};

export const openMapLink = (mapLink: string) => {
    try {
        useWebviewCallback(
            {
                action: EWebViewActions.INTENT,
                action_value: {
                    url: mapLink,
                },
            },
            () => {
                window.open(mapLink, '_blank');
            }
        );
    } catch (error) {
        console.log('Error opening map link : ', error);
    }
};

const stages = [ETrackingStages.REPAIR_IN_PROGRESS];

export const getRedirectDestination = (
    timelineData: any,
    orderId: string,
    query: Record<string, any>
): string => {
    // Build query params with orderId
    const queryParams = { ...query, orderId };

    const buildDestination = (path: string): string => {
        const queryString = new URLSearchParams(
            queryParams as Record<string, string>
        ).toString();
        return queryString ? `${path}?${queryString}` : path;
    };

    // Wrap timelineData to match Redux state shape expected by shared utils
    const garageDataShape = { timeline: timelineData };

    // Check if there are any pending approvals for damages in later stages
    if (checkIfMultipleDamageFlow(garageDataShape)) {
        return buildDestination(PageRoutes.JOBCARD);
    }

    const currentStage = timelineData?.latest_stage_details?.current_stage;

    // Check if customer has declined all the addons and is returning with same order_id
    if (
        !timelineData?.latest_stage_details?.data?.approved_damages?.length &&
        !timelineData?.latest_stage_details?.data?.pending_damages?.length &&
        stages.includes(currentStage)
    ) {
        return buildDestination('/no-selection');
    }

    if (currentStage) {
        // Render welcome page if stage has moved but it is not viewed
        if (
            currentStage !== 'WELCOME' &&
            !isWelcomeScreenViewed(garageDataShape)
        ) {
            return buildDestination(PageRoutes.WELCOME);
        }

        const route = getRoute(currentStage);
        if (route) {
            return buildDestination(route);
        }
    }

    return buildDestination('/');
};

const garageRedirectPath = '/';

// Fetch timeline and return redirect response based on stage
export const getTimelineRedirect = async (
    orderId: string,
    req: IncomingMessage,
    query: Record<string, any>
) => {
    const timelineData = await fetchGarageTimelineServer(orderId, req);

    // If timeline data is null or empty, redirect to home
    if (
        !timelineData ||
        (timelineData?.timeline === null &&
            timelineData?.latest_stage_details === null)
    ) {
        console.log('No timeline data found, redirecting to home');
        return {
            redirect: {
                destination: garageRedirectPath,
                permanent: false,
            },
        };
    }

    // Determine redirect destination based on timeline data
    const destination = getRedirectDestination(timelineData, orderId, query);
    console.log(`Redirecting to: ${destination}`);

    return {
        redirect: {
            destination,
            permanent: false,
        },
    };
};
